import { useEffect, useMemo, useState } from "react";

import { EnumeratedIconikAsset, IconikAsset } from "@/components/iconik/types";

import { useApi } from "../useApi";
import { loadPlayAssets } from "./useAssets";

/**
 * Enumerate cart items for display AND refresh their admin-driven fields.
 *
 * Cart items are snapshots persisted to localStorage, so the `previewFields`
 * baked in when an item was added go stale when an admin later enables/disables
 * a metadata field. The chat accordion never shows this because it always
 * re-fetches `/assets`; the cart must do the same to stay consistent.
 *
 * For each distinct play represented in the cart (items tagged with
 * `gameId`/`playId` at fetch time), this re-fetches the play via
 * {@link loadPlayAssets} (shared cache — usually no extra network call) and
 * overlays the fresh `previewFields` (and normalized description) onto the
 * matching cart item. Items that predate play-tagging, or whose asset is no
 * longer returned, keep their snapshot values.
 */
const useCartPreviewItems = (
  cartItems: IconikAsset[]
): EnumeratedIconikAsset[] => {
  const { apiGet } = useApi();
  const [freshByAssetId, setFreshByAssetId] = useState<
    Record<string, IconikAsset>
  >({});

  // Distinct plays present in the cart, as a stable string key so the effect
  // only re-runs when the set of plays changes (not on every render).
  const playsKey = useMemo(
    () =>
      Array.from(
        new Set(
          cartItems
            .filter((i) => i.gameId && i.playId)
            .map((i) => `${i.gameId}|${i.playId}`)
        )
      )
        .sort()
        .join(","),
    [cartItems]
  );

  useEffect(() => {
    let cancelled = false;
    const plays = playsKey ? playsKey.split(",") : [];
    if (plays.length === 0) {
      setFreshByAssetId({});
      return;
    }

    (async () => {
      const map: Record<string, IconikAsset> = {};
      await Promise.all(
        plays.map(async (key) => {
          const [gameId, playId] = key.split("|");
          try {
            const data = await loadPlayAssets(apiGet, gameId, playId);
            for (const asset of data.items) {
              map[asset.assetId] = asset;
            }
          } catch (error) {
            // A failed refresh leaves the affected items on their snapshot.
            console.error(error);
          }
        })
      );
      if (!cancelled) {
        setFreshByAssetId(map);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [playsKey, apiGet]);

  return useMemo(
    () =>
      cartItems.map((item, index) => {
        const fresh = freshByAssetId[item.assetId];
        return {
          ...item,
          // Overlay only the admin-driven fields that can go stale; keep the
          // snapshot for everything else (and when no fresh asset is found).
          ...(fresh
            ? {
                previewFields: fresh.previewFields,
                description: fresh.description,
              }
            : {}),
          index: index + 1,
        };
      }),
    [cartItems, freshByAssetId]
  );
};

export default useCartPreviewItems;
